import React, {useEffect, useMemo, useState} from 'react'
import axios from 'axios'
import LoadingOverlay from 'react-loading-overlay'
import {AdminDataTable, AdminTableSearch} from '../../components/AdminDataTable/AdminDataTable'
import {OrderDetail} from '../../components/OrderDetail/OrderDetail'
import '../Pedidos/Pedidos.css'
import './MisPedidos.css'

const money = new Intl.NumberFormat('es-MX', {style: 'currency', currency: 'MXN'})
const dateTime = new Intl.DateTimeFormat('es-MX', {dateStyle: 'medium', timeStyle: 'short'})
function MisPedidos() {
  const API_URL = process.env.REACT_APP_API_URL
  const [orders, setOrders] = useState([])
  const [detail, setDetail] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState({fecha_inicial: '', fecha_final: ''})

  const loadOrders = async (activeFilters = filters) => {
    setLoading(true)
    setError('')
    try {
      const {data} = await axios.get(`${API_URL}/mis-pedidos`, {params: activeFilters})
      setOrders(data.data || [])
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'No fue posible consultar tus pedidos.')
    } finally {
      setLoading(false)
    }
  }

  const viewOrder = async (id) => {
    setLoading(true)
    setError('')
    try {
      const {data} = await axios.get(`${API_URL}/mis-pedidos/${id}`)
      setDetail(data.data)
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'No fue posible consultar el pedido.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadOrders({fecha_inicial: '', fecha_final: ''})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const filteredOrders = useMemo(() => {
    const term = search.trim().toLocaleLowerCase('es-MX')
    if (!term) return orders
    return orders.filter((order) => `${order.folio}`.toLocaleLowerCase('es-MX').includes(term))
  }, [orders, search])

  const clearFilters = () => {
    const empty = {fecha_inicial: '', fecha_final: ''}
    setFilters(empty)
    loadOrders(empty)
  }

  const columns = [
    {name: 'Folio', selector: (row) => row.folio, sortable: true, cell: (row) => <strong className='pedido-folio'>{row.folio}</strong>, grow: 1.1},
    {id: 'fecha', name: 'Fecha', selector: (row) => new Date(row.fecha.replace(' ', 'T')).getTime(), sortable: true, cell: (row) => dateTime.format(new Date(row.fecha.replace(' ', 'T'))), grow: 1.4},
    {name: 'Vendedor asignado', selector: (row) => row.vendedorAsignadoNombre || '', sortable: true, wrap: true, grow: 1.3, cell: (row) => row.vendedorAsignadoNombre || 'Información no disponible'},
    {name: 'Productos', selector: (row) => Number(row.productos), sortable: true, cell: (row) => `${row.productos} ${Number(row.productos) === 1 ? 'producto' : 'productos'}`, center: true},
    {name: 'Total', selector: (row) => Number(row.total), sortable: true, right: true, cell: (row) => <strong>{money.format(row.total)}</strong>},
    {name: 'Acción', button: true, cell: (row) => <button type='button' className='btn btn-sm btn-light-primary' onClick={() => viewOrder(row.id)}>Ver</button>},
  ]

  const emptyState = (
    <div className='mis-pedidos-empty'>
      <strong>{search ? 'No encontramos pedidos.' : 'Aún no tienes pedidos.'}</strong>
      <span>{search ? 'Prueba con otro folio.' : 'Cuando levantes un pedido aparecerá aquí.'}</span>
    </div>
  )

  return (
    <LoadingOverlay active={loading} spinner text='Cargando...'>
      <OrderDetail detail={detail} onHide={() => setDetail(null)} showPriceList={false} />

      <div className='card card-custom card-stretch shadow mb-5 admin-list-card mis-pedidos-card'>
        <div className='card-header'><h3 className='card-title'>Mis Pedidos</h3></div>
        <div className='card-body'>
          <form className='pedido-filters' onSubmit={(event) => {event.preventDefault(); loadOrders()}}>
            <input className='form-control' type='date' name='fecha_inicial' value={filters.fecha_inicial} onChange={(event) => setFilters({...filters, fecha_inicial: event.target.value})} aria-label='Fecha inicial' />
            <input className='form-control' type='date' name='fecha_final' value={filters.fecha_final} onChange={(event) => setFilters({...filters, fecha_final: event.target.value})} aria-label='Fecha final' />
            <button className='btn btn-primary' type='submit'>Filtrar</button>
            <button className='btn btn-light' type='button' onClick={clearFilters}>Limpiar</button>
          </form>
          {error && <div className='alert alert-danger'>{error}</div>}
          <AdminTableSearch value={search} onChange={(event) => setSearch(event.target.value)} ariaLabel='Buscar en mis pedidos' placeholder='Buscar por folio...' />
          <AdminDataTable columns={columns} data={filteredOrders} pagination paginationPerPage={10} paginationRowsPerPageOptions={[10, 25, 50, 100]} defaultSortFieldId='fecha' defaultSortAsc={false} noDataComponent={emptyState} />
        </div>
      </div>
    </LoadingOverlay>
  )
}

export {MisPedidos}
